import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Lock, ArrowRight } from 'lucide-react';
import axios from 'axios';
import AuthShell from './AuthShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = ({ onAuthenticate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('session') === 'expired') {
      setInfo('Your session expired. Please sign in again.');
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, credentials);

      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        onAuthenticate(true);
        navigate('/admin/retreat/dashboard');
      }
    } catch {
      setError('Invalid username or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Admin Workspace"
      title="Sign in to the dashboard"
      subtitle="Restricted access — for retreat organizers only."
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-7"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-2 mb-5">
          <Lock className="w-3.5 h-3.5 text-white/40" />
          <p className="eyebrow text-white/40">Private Access</p>
        </div>

        <div className="space-y-3">
          <Input
            type="text"
            placeholder="Username"
            value={credentials.username}
            onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
            required
            autoFocus
            className="bg-white/[0.04] border-white/15 text-white placeholder:text-white/30 h-12 text-base focus-visible:ring-red-400/40"
            data-testid="admin-username"
          />
          <Input
            type="password"
            placeholder="Password"
            value={credentials.password}
            onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
            required
            className="bg-white/[0.04] border-white/15 text-white placeholder:text-white/30 h-12 text-base focus-visible:ring-red-400/40"
            data-testid="admin-password"
          />

          {error && (
            <p className="text-xs text-red-300 pl-1" data-testid="admin-login-error">
              {error}
            </p>
          )}
          {info && !error && (
            <p className="text-xs text-amber-200/90 pl-1" data-testid="admin-login-info">
              {info}
            </p>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full h-12 bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600 text-white font-semibold text-base btn-lift group"
            data-testid="admin-login-submit"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
            {!isLoading && (
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            )}
          </Button>
        </div>
      </form>
    </AuthShell>
  );
};

export default AdminLogin;
